import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './contexts/LanguageContext';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import DashboardPage from './pages/DashboardPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import CookiePage from './pages/CookiePage';
import DataDeletionPage from './pages/DataDeletionPage';
import SupportPage from './pages/SupportPage';
import QuidanalyzerPage from './pages/QuidanalyzerPage';
import QuidanalyzerSuccessPage from './pages/QuidanalyzerSuccessPage';
import QuidanalyzerAccountPage from './pages/QuidanalyzerAccountPage';
import './App.css';

// Reads stored language hint (or default 'ar') and redirects '/' to the locale-prefixed root.
const RootRedirect = () => {
  let lang = 'ar';
  try {
    const stored = window.localStorage.getItem('quidory.language');
    if (stored === 'en' || stored === 'ar') lang = stored;
  } catch {}
  return <Navigate to={`/${lang}`} replace />;
};

// Redirect any unprefixed marketing path to the stored-locale version (preserves shared old links).
const UnprefixedRedirect = () => {
  const { pathname, search, hash } = useLocation();
  let lang = 'ar';
  try {
    const stored = window.localStorage.getItem('quidory.language');
    if (stored === 'en' || stored === 'ar') lang = stored;
  } catch {}
  return <Navigate to={`/${lang}${pathname}${search}${hash}`} replace />;
};

// Locale-scoped route group — same children, mounted under either /ar or /en.
const LocaleRoutes = () => (
  <Routes>
    <Route element={<Layout />}>
      <Route index element={<LandingPage />} />
      <Route path="features" element={<FeaturesPage />} />
      <Route path="pricing" element={<PricingPage />} />
      <Route path="about" element={<AboutPage />} />
      <Route path="contact" element={<ContactPage />} />
      <Route path="login" element={<LoginPage />} />
      <Route path="signup" element={<SignupPage />} />
      <Route path="privacy" element={<PrivacyPage />} />
      <Route path="terms" element={<TermsPage />} />
      <Route path="cookie-policy" element={<CookiePage />} />
      <Route path="data-deletion" element={<DataDeletionPage />} />
      <Route path="support" element={<SupportPage />} />
      <Route path="quidanalyzer" element={<QuidanalyzerPage />} />
      <Route path="quidanalyzer/success" element={<QuidanalyzerSuccessPage />} />
      <Route path="quidanalyzer/account" element={<QuidanalyzerAccountPage />} />
    </Route>
  </Routes>
);

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
        <BrowserRouter>
          <LanguageProvider>
            <Routes>
              {/* Root → locale redirect */}
              <Route path="/" element={<RootRedirect />} />

              {/* Locale-prefixed marketing site */}
              <Route path="/ar/*" element={<LocaleRoutes />} />
              <Route path="/en/*" element={<LocaleRoutes />} />

              {/* Mock dashboard — locale-agnostic internal app */}
              <Route path="/dashboard" element={<DashboardPage />} />

              {/* Backward-compat: redirect old unprefixed marketing URLs */}
              <Route path="/features"       element={<UnprefixedRedirect />} />
              <Route path="/pricing"        element={<UnprefixedRedirect />} />
              <Route path="/about"          element={<UnprefixedRedirect />} />
              <Route path="/contact"        element={<UnprefixedRedirect />} />
              <Route path="/login"          element={<UnprefixedRedirect />} />
              <Route path="/signup"         element={<UnprefixedRedirect />} />
              <Route path="/privacy"        element={<UnprefixedRedirect />} />
              <Route path="/terms"          element={<UnprefixedRedirect />} />
              <Route path="/cookie-policy"  element={<UnprefixedRedirect />} />
              <Route path="/data-deletion"  element={<UnprefixedRedirect />} />
              <Route path="/support"        element={<UnprefixedRedirect />} />
              <Route path="/quidanalyzer"   element={<UnprefixedRedirect />} />
              <Route path="/quidanalyzer/success" element={<UnprefixedRedirect />} />
              <Route path="/quidanalyzer/account" element={<UnprefixedRedirect />} />

              {/* Final fallback */}
              <Route path="*" element={<RootRedirect />} />
            </Routes>
          </LanguageProvider>
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
